/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {ColDef} from 'ag-grid-community';
import {isEUProduction} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {
  Device,
  GenericObject,
  serviceStateType1
} from '@ion/core/src/types/entities';
import {sortByString} from '@ion/core/src/utils/ArrayUtil';
import {resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {AG_CLASSES} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {DLSTheme} from '@ion/dls/src/types/types';
import useServiceEnablementFeatures from './useServiceEnablementFeatures';

export interface ServiceTabType {
  Features: GenericObject;
  device: Device;
  tabIndex: Number;
  hasTeleCryptEnabled?: boolean;
  hasCARBEnabled?: boolean;
  isCtcAdmin?: boolean;
};

enum ServiceType {
  ICN = 1,
  BLE = 3,
  IVT = 4,
  CARGO = 6,
  TPMS = 7,
  DOOR = 8,
  WETS = 9,
  BMS = 10,
  LMS = 13,
  WMS = 14,
  EA = 17,
  EA_STATUS = 18,
  STATUS_EA = 20,
  SENTINEL = 21,
  TELECRYPT = 24,
  VESCOUT = 23,
  CARB = 25,
  MODBUS = 26
};

// Define your overrides outside the component
const SERVICE_TO_FEATURE_MAP: Record<string, string> = {
  SENTINEL : 'DDSAR'
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  servicesGrid : {
    '& .ag-center-cols-viewport' : {
      overflowX : 'hidden !important'
    },
    '& .ag-popup' : {
      height : 'auto'
    },
    '&&' : {
      height                   : '100%',
      [AG_CLASSES.BODY_LAYOUT] : {
        overflowY : 'auto'
      }
    }
  },
  detailsTab : {
    height : '100%'
  },
  gridContainer : {
    marginTop : '20px',
    height    : 'calc(100vh - 350px)'
  }
}));

/**
 * ServicesTab component that displays the Device services
 * data for the selected Device Details tab.
 */
export const ServicesTab = ({
  device,
  Features,
  isCtcAdmin,
  tabIndex = 0,
  hasCARBEnabled = false,
  hasTeleCryptEnabled = false
}: ServiceTabType) => {
  const classes                               = useStyles();
  const [gridApi, setGridApi]                 = useState(null);
  const [hasItems, setHasItems]               = useState<boolean>(false);
  const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
  const serviceTranslations                   = translate('common:serviceEnablement', {returnObjects: true});
  const defaultTranslations                   = translate('admin-devices:view.services.defaultValue', {returnObjects: true});

  const serviceMappings = useServiceEnablementFeatures(Features);

  const {servicesInfo, servicesLoading, loading: isServicesLoading} = useSelector(({devices: {view}}) => view);
  const {idReport}     = device?.idReport?.device || {};
  const services       = idReport?.deviceServices?.services;
  const vasSummary     = servicesInfo?.results[0]?.vasSummary;
  const {features}      = device || {};
  
  const getServiceSupport = (vasSummary: any, services: any[], mapping: any) => {
    const vasKey  = mapping?.vasKey;
    const service = services?.find(item => mapping?.typeId === item?.typeId);
    const state   = service?.state;

    if (vasSummary && vasKey) {
      return vasSummary?.[vasKey]?.status;
    }
    if (state) {
      return serviceStateType1?.[state];
    }
  };

  const serviceListData = useMemo(() => {
    const extendedMappings = [
      ...serviceMappings,
      {name: 'VESCOUT'}
    ];
    /**
     * This check is for Prevent the CARB Services in EU environment. For timing
     */
    const finalMappings = isCtcAdmin && isEUProduction()
      ? extendedMappings.filter(
        ({id}) => id !== Features?.getCARBFeature())
      : extendedMappings;

    return finalMappings?.map?.((service, index) => {
      const {name}         = service || {};
      const translationKey = name?.toLowerCase();
      const enumKey        = name?.toUpperCase();
      const featureKey     = SERVICE_TO_FEATURE_MAP[enumKey] ?? enumKey;
      const typeId         = ServiceType[enumKey as keyof typeof ServiceType];

      const mapping = {
        vasKey : translationKey,
        typeId
      };

      return {
        id             : index + 1,
        serviceName    : serviceTranslations?.[translationKey],
        serviceStatus  : features?.[featureKey] ?? defaultTranslations?.disabled,
        serviceSupport : getServiceSupport(vasSummary, services, mapping) ?? defaultTranslations?.notAvailable
      };
    });
  }, [features, services, vasSummary, serviceMappings, isServicesLoading, servicesLoading, servicesInfo]);

  useEffect(() => {
    gridApi?.hideOverlay();
    if (gridApi && !hasItems) {
      gridApi.showNoRowsOverlay();
    }
  }, [hasItems]);

  useEffect(() => {
    if (!services) {
      setRefreshRequired(true);
    }
  }, [serviceListData]);

  /**
   * Takes a list of services and constructs a list that
   * can be easily consumed by the IonGrid API
   */
  const constructServicesGridRows = useCallback(function(services: GenericObject[]): GridResult[] {
    const gridItems = services?.map(service => ({
      ...service,
      serviceNameUI    : service?.serviceName,
      serviceStatusUI  : service?.serviceStatus,
      serviceSupportUI : service?.serviceSupport
    })) as GridResult[];
    setHasItems(!!gridItems?.length);
    return gridItems;
  }, []);

  // Provides reference to the grid's api.
  const onGridReady = useCallback(function(api) {
    setGridApi(api);
  }, []);

  const getGridHeight = useCallback(function() {
    return classes.servicesGrid;
  }, []);

  // memoize the sorted data set, only performing the sort when serviceListData changes ...
  const items = useMemo(() => (
    sortByString(serviceListData, 'serviceName')
  ), [serviceListData]);

  const isLoading = servicesLoading || (servicesInfo === null && isServicesLoading);

  return (
    <div id={`detailsTab_${tabIndex}`} className={classes.detailsTab}>
      <div className={classes.gridContainer}>
        <IonGrid omitHeader
          rowModelType={RowModelTypes.ClientSide}
          rowData={items}
          isLoading={isLoading}
          totalRows={serviceListData?.length}
          augmentItems={constructServicesGridRows}
          columnDefinitions={getColumnDefinitions()}
          refreshRequired={refreshRequired}
          setRefreshRequired={setRefreshRequired}
          checkboxesEnabled={false}
          showHeaderCheckbox={false}
          viewSubGroups={false}
          rowMenusEnabled={false}
          rowHeight={40}
          onGridReady={onGridReady}
          getGridHeight={getGridHeight}
          paginationEnabled
          showCustomPaginationBar
          pageOffset
          pageSizes={PAGE_SIZES} />
      </div>
    </div>
  );
};

/**
 * Returns column definitions for desktop Services grid
 */
const getColumnDefinitionsDesktop = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:view.services.serviceName'),
    field      : 'serviceNameUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.services.serviceStatus'),
    field      : 'serviceStatusUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.services.serviceSupport'),
    field      : 'serviceSupportUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};

/**
 * Returns column definitions for mobile Services grid
 */
const getColumnDefinitionsMobile = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:view.services.serviceName'),
    field      : 'serviceNameUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.services.serviceStatus'),
    field      : 'serviceStatusUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.services.serviceSupport'),
    field      : 'serviceSupportUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};

const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile())
});

export default ServicesTab;
